// Команды таблицы протокола (011: contracts/api.md, Command и CommandAccepted; ADR-0032; 012:
// contracts/api.md, «Виды команды»). Ключ клиента — commandId (uuid); номер операции по-прежнему
// создаёт только сервер (принцип XIV). Одна принятая команда — одна ревизия протокола. Схемы
// строгие: лишнее поле тела — REC-007 (сходимость 011).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema } from '../kernel/common.ts';
import {
  AssigneeSchema,
  CellBorderSchema,
  CellColorSchema,
  CellMergeRoleSchema,
  CellStyleSchema,
  CellStylesSchema,
  ExtraFieldKeySchema,
  ITEM_ASSIGNEES_MAX,
  ITEM_CONTRACTORS_MAX,
  ITEM_NOTE_MAX,
  ITEM_TITLE_MAX,
  ItemFieldSchema,
  ItemRowSchema,
  PartySchema,
  WorkStateSchema,
  fieldValueMax,
  partyFields,
  withoutControlChars,
} from './item.ts';
import { ProtocolColumnSchema } from './protocol.ts';
import {
  VERSION_REVERT_CELLS_MAX,
  VERSION_REVERT_ROWS_MAX,
  VersionCellRefSchema,
} from './versions.ts';

/** Сколько правок в одной команде cells.edit. */
export const CELLS_EDIT_MAX = 500;
/** Сколько знаков во всех значениях одной команды cells.edit (012: research, Р18). */
export const CELLS_EDIT_CHARS_MAX = 1_000_000;
/** Сколько пустых строк вставляет одна команда rows.insert (TREE-05). */
export const ROWS_INSERT_MAX = 500;
/** Сколько строк или ветвей называет одна команда дерева (FR-037). */
export const BRANCHES_MAX = 500;
/** Сколько новых пунктов создаёт одна копия ветвей; больше — MIN-005 (FR-037). */
export const COPY_ITEMS_MAX = 2000;
/** Предел тела запроса команды: 500 правок по 1 000 000 знаков в сумме с запасом (Р18). */
export const COMMAND_BODY_LIMIT = 4 * 1024 * 1024;
/** Сколько правок в одной пакетной правке cells.batch (013: research, Р4, Р18). */
export const CELLS_BATCH_MAX = 5000;
/** Сколько ячеек в одной команде оформления cells.style (013: Р5). */
export const CELLS_STYLE_MAX = 5000;
/** Подпись дополнительного поля и число полей протокола (013: Р11). */
export const FIELD_LABEL_MAX = 80;
export const EXTRA_FIELDS_MAX = 999;

const RevisionSchema = z.number().int().min(1);

/** Номер новой строки, который выдаёт клиент: uuid v7 (сходимость 011). */
export const NewItemIdSchema = z.uuid({ version: 'v7' });

/** Одна правка ячейки; предел значения — по полю (название, примечание, остальное). */
export const CellEditSchema = z
  .strictObject({
    itemId: IdSchema,
    field: ItemFieldSchema,
    value: z.string().max(ITEM_NOTE_MAX),
  })
  .refine((edit) => edit.value.length <= fieldValueMax(edit.field), {
    message: `Значение поля — не длиннее предела поля (${ITEM_TITLE_MAX} знаков, контрагент — 500)`,
    path: ['value'],
  })
  .refine((edit) => withoutControlChars(edit.value) === edit.value, {
    message: 'В значении поля — управляющий знак',
    path: ['value'],
  });
export type CellEdit = z.infer<typeof CellEditSchema>;

export const CellsEditCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('cells.edit'),
  edits: z
    .array(CellEditSchema)
    .min(1)
    .max(CELLS_EDIT_MAX)
    .refine(
      (edits) => edits.reduce((sum, edit) => sum + edit.value.length, 0) <= CELLS_EDIT_CHARS_MAX,
      { message: `Во всех значениях команды — не больше ${CELLS_EDIT_CHARS_MAX} знаков` },
    ),
});

/** Поля команд, которые меняют дерево: база — ревизия протокола и ревизия дерева. */
const structureBase = {
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  baseStructureRevision: RevisionSchema,
} as const;

/**
 * Куда встают новые строки (TREE-05): перед строкой, после всей её ветви, в конец её детей
 * или в конец дерева (root — без строки-якоря).
 */
export const insertPlaces = ['before', 'after', 'inside', 'root'] as const;
export type InsertPlace = (typeof insertPlaces)[number];

/** Куда встают перенесённые ветви (TREE-09): перед целью, после всей её ветви, в конец детей. */
export const movePlaces = ['before', 'after', 'inside'] as const;
export type MovePlace = (typeof movePlaces)[number];

const BranchIdsSchema = z.array(IdSchema).min(1).max(BRANCHES_MAX);

/** Пустые строки в выбранное место; itemIds — uuid v7 клиента (показ до ответа). */
export const RowsInsertCommandSchema = z
  .strictObject({
    ...structureBase,
    kind: z.literal('rows.insert'),
    anchorItemId: IdSchema.nullable(),
    place: z.enum(insertPlaces),
    itemIds: z.array(NewItemIdSchema).min(1).max(ROWS_INSERT_MAX),
  })
  .refine((command) => (command.place === 'root') === (command.anchorItemId === null), {
    message: 'Строка-якорь нужна для всех мест, кроме конца дерева',
    path: ['anchorItemId'],
  });

/** Решение неопределённого родителя: кандидат или корень (parentId = null). */
export const ParentResolveCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('parent.resolve'),
  itemId: IdSchema,
  parentId: IdSchema.nullable(),
});

/** Перенос ветвей (перенос мышью, вставка вырезанного); номера пунктов сохраняются. */
export const BranchesMoveCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('branches.move'),
  itemIds: BranchIdsSchema,
  targetId: IdSchema,
  place: z.enum(movePlaces),
});

/** «Вложить» (TREE-07). */
export const BranchesIndentCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('branches.indent'),
  itemIds: BranchIdsSchema,
});

/** «Поднять уровень» (TREE-08). */
export const BranchesOutdentCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('branches.outdent'),
  itemIds: BranchIdsSchema,
});

/** Как появилась копия: вставка из буфера ветвей или «Дублировать ветви» (TREE-11). */
export const copyModes = ['paste', 'duplicate'] as const;
export type CopyMode = (typeof copyModes)[number];

/**
 * Содержимое ветви на момент Copy; источник и служебные поля копии задаёт сервер. Позиции здесь —
 * только порядок соседей: ключи копии сервер считает сам; ссылка разделения обязана совпасть
 * с базой; значение длиннее предела правки принимается, только если равно значению пункта.
 */
export const BranchSnapshotItemSchema = ItemRowSchema.pick({
  id: true,
  parentId: true,
  position: true,
  title: true,
  noteHtml: true,
  dueRaw: true,
  responsibleRaw: true,
  extra: true,
  splitFromId: true,
})
  // Оформление ячеек на момент Copy (013: FR-018); старый клиент его не передаёт. Поля 014
  // (FR-007, FR-014, FR-020, FR-026) — тоже необязательны для старого клиента.
  .extend({
    styles: CellStylesSchema.optional(),
    assignees: z.array(AssigneeSchema).max(ITEM_ASSIGNEES_MAX).optional(),
    startRaw: z.string().nullable().optional(),
    /**
     * Контрагенты на момент Copy (027: FR-007) — список, как ответственные. Строка — клиент до 027
     * (очередь в браузере): текст контрагента, сервер разбирает его правилом поля.
     */
    contractor: z
      .union([z.array(PartySchema).max(ITEM_CONTRACTORS_MAX), z.string()])
      .nullable()
      .optional(),
    actualEndRaw: z.string().nullable().optional(),
    workState: WorkStateSchema.nullable().optional(),
    workNote: z.string().nullable().optional(),
  })
  .strict()
  .refine(
    (item) =>
      [
        item.title,
        item.noteHtml,
        item.dueRaw,
        item.responsibleRaw,
        item.startRaw ?? null,
        typeof item.contractor === 'string' ? item.contractor : null,
        item.actualEndRaw ?? null,
        item.workNote ?? null,
        ...Object.values(item.extra),
      ].every((value) => value === null || withoutControlChars(value) === value),
    { message: 'В снимке ветви — управляющий знак' },
  );
export type BranchSnapshotItem = z.infer<typeof BranchSnapshotItemSchema>;

/** Копии ветвей с новыми номерами после всей ветви цели; номера выдаёт сервер (DATA-10). */
export const BranchesCopyCommandSchema = z
  .strictObject({
    ...structureBase,
    kind: z.literal('branches.copy'),
    sourceIds: BranchIdsSchema,
    targetId: IdSchema,
    mode: z.enum(copyModes),
    snapshot: z.array(BranchSnapshotItemSchema).min(1).max(COPY_ITEMS_MAX).optional(),
  })
  .refine((command) => command.snapshot === undefined || command.mode === 'paste', {
    message: 'Снимок ветви используется только при вставке копии',
    path: ['snapshot'],
  });

/** Архив ветвей или архив строк с подъёмом детей (TREE-12); удаления нет. */
export const BranchesArchiveCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('branches.archive'),
  itemIds: BranchIdsSchema,
  keepChildren: z.boolean(),
});

/** Возврат архивных строк с потомками той же ревизии архива (TREE-12). */
export const BranchesRestoreCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('branches.restore'),
  itemIds: BranchIdsSchema,
});

/** Название нового пункта при разделении: непустое после обрезки (FR-031). */
const SplitTitleSchema = z
  .string()
  .max(ITEM_TITLE_MAX)
  .refine((title) => title.trim().length > 0, { message: 'Название нового пункта пустое' })
  .refine((title) => withoutControlChars(title) === title, {
    message: 'В названии — управляющий знак',
  });

/** Имя черновика человека, который создаёт поповер «Ответственные» (014: FR-010). */
export const DRAFT_NAME_MAX = 200;

const noControl = (text: string) => withoutControlChars(text) === text;

/** Ответственный команды: запись справочника, прежний текст или новый черновик человека. */
export const AssigneeInputSchema = z.union([
  z.strictObject({ recordId: IdSchema }),
  z.strictObject({
    raw: z.string().min(1).max(ITEM_TITLE_MAX).refine(noControl, { message: 'Управляющий знак' }),
  }),
  z.strictObject({
    draftName: z
      .string()
      .max(DRAFT_NAME_MAX)
      .refine((name) => name.trim().length > 0, { message: 'Введите имя' })
      .refine(noControl, { message: 'Управляющий знак' }),
  }),
]);
export type AssigneeInput = z.infer<typeof AssigneeInputSchema>;
/** Элемент списка команды «Ответственного» или «Контрагента» (027): запись, текст, черновик. */
export const PartyInputSchema = AssigneeInputSchema;
export type PartyInput = AssigneeInput;

/** «Разделить пункт…» (TREE-13): сосед после всей ветви исходного, ссылка на исходный. */
export const ItemSplitCommandSchema = z
  .strictObject({
    ...structureBase,
    kind: z.literal('item.split'),
    itemId: IdSchema,
    title: SplitTitleSchema,
    /** Не передано — копировать исходных; [] / null — очистить у нового пункта. */
    assignees: z.array(AssigneeInputSchema).max(ITEM_ASSIGNEES_MAX).optional(),
    dueRaw: z
      .string()
      .max(ITEM_TITLE_MAX)
      .refine(noControl, { message: 'Управляющий знак' })
      .nullable()
      .optional(),
    /** Новое и увиденное названия передаются вместе; старый Split не меняет исходное. */
    firstTitle: z
      .string()
      .max(ITEM_TITLE_MAX)
      .refine((title) => withoutControlChars(title) === title, {
        message: 'В названии — управляющий знак',
      })
      .optional(),
    // Это точное значение из ответа ItemRow: импортированные названия не обрезаются.
    expectedFirstTitle: z
      .string()
      .refine((title) => withoutControlChars(title) === title, {
        message: 'В исходном названии — управляющий знак',
      })
      .optional(),
  })
  .refine(
    (command) => (command.firstTitle === undefined) === (command.expectedFirstTitle === undefined),
    {
      message: 'Новое и увиденное названия исходного пункта передаются вместе',
      path: ['expectedFirstTitle'],
    },
  );

/** Зачем пакетная правка: вставка, вырезание, очистка, заполнение, протягивание, замена (013). */
export const batchReasons = ['paste', 'cut', 'clear', 'fill', 'tile', 'replace'] as const;
export type BatchReason = (typeof batchReasons)[number];

/** Правка ячейки пакета: значение и (или) оформление; `style: null` снимает оформление поля. */
export const BatchEditSchema = z
  .strictObject({
    itemId: IdSchema,
    field: ItemFieldSchema,
    value: z.string().max(ITEM_NOTE_MAX).optional(),
    style: CellStyleSchema.nullable().optional(),
  })
  .refine((edit) => edit.value !== undefined || edit.style !== undefined, {
    message: 'Нужно значение или оформление ячейки',
    path: ['value'],
  })
  .refine((edit) => edit.value === undefined || edit.value.length <= fieldValueMax(edit.field), {
    message: `Значение поля — не длиннее предела поля (${ITEM_TITLE_MAX} знаков, контрагент — 500)`,
    path: ['value'],
  })
  .refine((edit) => edit.value === undefined || withoutControlChars(edit.value) === edit.value, {
    message: 'В значении поля — управляющий знак',
    path: ['value'],
  });
export type BatchEdit = z.infer<typeof BatchEditSchema>;

const cellKey = (cell: { itemId: string; field: string }) => `${cell.itemId}:${cell.field}`;
const uniqueCells = (cells: readonly { itemId: string; field: string }[]) =>
  new Set(cells.map(cellKey)).size === cells.length;

/**
 * Пакетная правка (013: FR-033, research Р4): вставка, вырезание, очистка, заполнение,
 * протягивание и замена — одна команда, всё или ничего. Новые строки (`rows`) встают перед
 * якорем или после его ветви (по умолчанию), с его родителем; нужна ревизия дерева.
 */
export const CellsBatchCommandSchema = z
  .strictObject({
    commandId: IdSchema,
    baseRevision: RevisionSchema,
    baseStructureRevision: RevisionSchema.optional(),
    kind: z.literal('cells.batch'),
    reason: z.enum(batchReasons),
    edits: z
      .array(BatchEditSchema)
      .min(1)
      .max(CELLS_BATCH_MAX)
      .refine(uniqueCells, { message: 'Ячейка указана дважды' })
      .refine(
        (edits) =>
          edits.reduce((sum, edit) => sum + (edit.value?.length ?? 0), 0) <= CELLS_EDIT_CHARS_MAX,
        { message: `Во всех значениях команды — не больше ${CELLS_EDIT_CHARS_MAX} знаков` },
      ),
    rows: z
      .strictObject({
        anchorItemId: IdSchema,
        place: z.enum(['before', 'after']).optional(),
        itemIds: z.array(NewItemIdSchema).min(1).max(ROWS_INSERT_MAX),
      })
      .optional(),
  })
  .refine((command) => command.rows === undefined || command.baseStructureRevision !== undefined, {
    message: 'Новые строки передаются вместе с ревизией дерева',
    path: ['baseStructureRevision'],
  });

/** Заплата оформления: свойства (`false` / `null` снимают свойство) или «снять всё». */
export const StylePatchSchema = z.union([
  z.strictObject({
    set: z
      .strictObject({
        b: z.boolean().optional(),
        i: z.boolean().optional(),
        s: z.boolean().optional(),
        color: CellColorSchema.nullable().optional(),
        fill: CellColorSchema.nullable().optional(),
      })
      .refine((set) => Object.keys(set).length > 0, { message: 'Нечего менять' }),
  }),
  z.strictObject({ clear: z.literal(true) }),
]);
export type StylePatch = z.infer<typeof StylePatchSchema>;

/** Оформление ячеек кнопками ленты (013: FR-015, FR-035, Р5); конфликт — по оформлению поля. */
export const CellsStyleCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('cells.style'),
  cells: z
    .array(z.strictObject({ itemId: IdSchema, field: ItemFieldSchema }))
    .min(1)
    .max(CELLS_STYLE_MAX)
    .refine(uniqueCells, { message: 'Ячейка указана дважды' }),
  patch: StylePatchSchema,
});

/**
 * Границы ячеек (017: research, Р1, Р3): стороны каждой затронутой ячейки целиком — после «Границ ▾»
 * по диапазону (ядро `borderPatches`), соседи за краем при «Очистить» — в том же списке; `null`
 * снимает все стороны. Конфликт — по оформлению поля, как у `cells.style`.
 */
export const CellsBordersCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('cells.borders'),
  cells: z
    .array(
      z.strictObject({
        itemId: IdSchema,
        field: ItemFieldSchema,
        border: CellBorderSchema.nullable(),
      }),
    )
    .min(1)
    .max(CELLS_STYLE_MAX)
    .refine(uniqueCells, { message: 'Ячейка указана дважды' }),
});

/**
 * Объединить и разъединить ячейки (017: research, Р2): роли ячеек после команды (ядро `mergePatch`,
 * `unmergePatch`; `null` — ячейка вне объединения) и значения той же командой — копия значения
 * начала в вертикальную цепочку, очистка прочих. Конфликт — по `merge.<поле>` и полям значений.
 */
export const CellsMergeCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('cells.merge'),
  /** true — «Объединить», false — «Разъединить» (подпись ленты и журнала). */
  merged: z.boolean(),
  cells: z
    .array(
      z.strictObject({
        itemId: IdSchema,
        field: ItemFieldSchema,
        role: CellMergeRoleSchema.nullable(),
      }),
    )
    .min(1)
    .max(CELLS_STYLE_MAX)
    .refine(uniqueCells, { message: 'Ячейка указана дважды' }),
  values: z
    .array(BatchEditSchema)
    .max(CELLS_BATCH_MAX)
    .refine(uniqueCells, { message: 'Ячейка указана дважды' })
    .optional(),
});

/** «Скрыть в протоколе» и «Показать в протоколе» (013: FR-031, Р14) — ветви целиком, у всех. */
export const RowsHideCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('rows.hide'),
  itemIds: BranchIdsSchema,
  hidden: z.boolean(),
});

/** Сколько пунктов в одной команде полей пункта (014: contracts/api.md). */
export const ITEM_FIELDS_ITEMS_MAX = 5000;
/** Пояснение к состоянию работы (014: FR-020). */
export const WORK_NOTE_MAX = 2000;

const ItemFieldsIdsSchema = z
  .array(IdSchema)
  .min(1)
  .max(ITEM_FIELDS_ITEMS_MAX)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Пункт указан дважды' });

/** Ответственные пунктов из поповера (014: FR-008): список целиком, одно событие на пункт. */
export const ItemAssigneesCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('item.assignees'),
  itemIds: ItemFieldsIdsSchema,
  assignees: z.array(AssigneeInputSchema).max(ITEM_ASSIGNEES_MAX),
});

/** Контрагенты пунктов из поповера (027: FR-004): список целиком, одно событие на пункт. */
export const ItemContractorsCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('item.contractors'),
  itemIds: ItemFieldsIdsSchema,
  contractors: z.array(PartyInputSchema).max(ITEM_CONTRACTORS_MAX),
});

/**
 * Ключ элемента списка в команде (027: research, Р5): запись — `record:<номер>`, текст —
 * `text:<ключ имени>` (assigneeNameKey ядра).
 */
export const PartyKeySchema = z
  .string()
  .max(ITEM_TITLE_MAX)
  .regex(/^(record:[0-9a-f-]{36}|text:.+)$/su)
  .refine(noControl, { message: 'Управляющий знак' });

/**
 * «Перенести в Контрагент / Ответственный» и «Заменить в пунктах…» (027: FR-006, FR-017; research,
 * Р5): из поля `field` убрать запись или текст `key` во всех текущих пунктах протокола (или
 * в `itemIds`), в поле `to.field` поставить запись `to.recordId`; `null` — ту же запись или текст,
 * разобранный правилом поля. Одна команда, одна отмена.
 */
export const PartiesReplaceCommandSchema = z
  .strictObject({
    commandId: IdSchema,
    baseRevision: RevisionSchema,
    kind: z.literal('parties.replace'),
    field: z.enum(partyFields),
    key: PartyKeySchema,
    to: z.strictObject({ field: z.enum(partyFields), recordId: IdSchema.nullable() }),
    itemIds: ItemFieldsIdsSchema.optional(),
  })
  .refine((command) => command.to.field !== command.field || command.to.recordId !== null, {
    message: 'Замена в том же поле — только на запись',
    path: ['to', 'recordId'],
  });

/** Строк вкладки в одном «Перенести в …» (0.20.1): выбор флажками — одна команда. */
export const PARTIES_MOVE_KEYS_MAX = 100;
/** Частей «Разделить…» (0.20.1). */
export const PARTY_SPLIT_PARTS_MAX = 10;

/**
 * «Перенести в Контрагент / Ответственный» вкладки (0.20.1, решение владельца 30.10.2026): записи
 * и тексты `keys` поля `field` — во второе поле во всех текущих пунктах протокола. Запись другого
 * вида сначала меняет вид в справочнике — как «Сменить вид» карточки, с тем же правом; текст —
 * по правилу поля назначения. Одна команда, одна отмена.
 */
export const PartiesMoveCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('parties.move'),
  field: z.enum(partyFields),
  keys: z
    .array(PartyKeySchema)
    .min(1)
    .max(PARTIES_MOVE_KEYS_MAX)
    .refine((keys) => new Set(keys).size === keys.length, { message: 'Строка указана дважды' }),
});

/**
 * «Разделить…» (0.20.1): запись или текст `key` поля `field` во всех текущих пунктах протокола
 * на месте заменяется частями `parts` — запись, прежний текст или новый черновик вида поля;
 * повторы — один раз. Ошибка разбора «два человека в одной записи». Одна команда, одна отмена.
 */
export const PartiesSplitCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('parties.split'),
  field: z.enum(partyFields),
  key: PartyKeySchema,
  parts: z.array(PartyInputSchema).min(2).max(PARTY_SPLIT_PARTS_MAX),
});

/** Ключи списка без повторов (028). */
const PartyKeysSchema = (min: number, max: number) =>
  z
    .array(PartyKeySchema)
    .min(min)
    .max(max)
    .refine((keys) => new Set(keys).size === keys.length, { message: 'Строка указана дважды' });

/**
 * Правка списка поля в пунктах (028: FR-008, FR-013, FR-014; research, Р1): `set` — список целиком,
 * как `item.assignees` (проверка конфликта поля); `add` и `remove` — по текущему списку пункта, без
 * проверки конфликта: сначала убрать ключи `remove`, затем добавить в конец `add`, которых нет.
 */
export const PartiesChangeSchema = z.union([
  z.strictObject({ set: z.array(PartyInputSchema).max(ITEM_ASSIGNEES_MAX) }),
  z
    .strictObject({
      add: z.array(PartyInputSchema).max(ITEM_ASSIGNEES_MAX),
      remove: PartyKeysSchema(0, ITEM_ASSIGNEES_MAX),
    })
    .refine((change) => change.add.length > 0 || change.remove.length > 0, {
      message: 'Нечего добавить или снять',
    }),
]);
export type PartiesChange = z.infer<typeof PartiesChangeSchema>;

/**
 * «Ответственный» и «Контрагент» выбранных пунктов одной командой (028: FR-008, FR-013): поповер
 * на нескольких пунктах (добавить и снять) и флажок «Добавить «…» в Контрагент» (список
 * ответственных и контрагент — одной правкой, одной отменой). Одно событие на поле пункта.
 */
export const ItemPartiesCommandSchema = z
  .strictObject({
    commandId: IdSchema,
    baseRevision: RevisionSchema,
    kind: z.literal('item.parties'),
    itemIds: ItemFieldsIdsSchema,
    assignees: PartiesChangeSchema.optional(),
    contractors: PartiesChangeSchema.optional(),
  })
  .refine((command) => command.assignees !== undefined || command.contractors !== undefined, {
    message: 'Нет поля для правки',
  });

/**
 * «Объединить…» вкладки (028: FR-024, FR-025; research, Р2): во всех текущих пунктах протокола
 * строки `keys` поля `field` заменяются строкой `keep` на месте первой из них; повторы — один раз.
 * Если среди `keys` есть запись справочника, `keep` — запись. Две и больше записей — пары «тот
 * же?» в справочнике. Одна команда, одна отмена.
 */
export const PartiesMergeCommandSchema = z
  .strictObject({
    commandId: IdSchema,
    baseRevision: RevisionSchema,
    kind: z.literal('parties.merge'),
    field: z.enum(partyFields),
    keys: PartyKeysSchema(2, PARTIES_MOVE_KEYS_MAX),
    keep: PartyKeySchema,
  })
  .refine((command) => command.keys.includes(command.keep), {
    message: 'Оставляемая строка — одна из объединяемых',
    path: ['keep'],
  })
  .refine(
    (command) =>
      command.keep.startsWith('record:') || !command.keys.some((key) => key.startsWith('record:')),
    { message: 'Остаться может только запись справочника', path: ['keep'] },
  );

/**
 * Состояние работы и пояснение из поповера (014: FR-021): одно событие на пункт. 030 (research Р6):
 * «Выполнено» из «Моих задач» ставит в той же команде и «Фактическое окончание» — сегодня;
 * нет поля — фактическое окончание не меняется.
 */
export const ItemWorkStateCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('item.workState'),
  itemIds: ItemFieldsIdsSchema,
  state: WorkStateSchema.nullable(),
  note: z.string().max(WORK_NOTE_MAX).refine(noControl, { message: 'Управляющий знак' }),
  actualEndRaw: z
    .string()
    .max(ITEM_TITLE_MAX)
    .refine(noControl, { message: 'Управляющий знак' })
    .optional(),
});

/** Подпись дополнительного поля: непустая после обрезки, без управляющих знаков (013: Р11). */
const FieldLabelSchema = z
  .string()
  .max(FIELD_LABEL_MAX)
  .refine((label) => label.trim().length > 0, { message: 'Введите название' })
  // Подпись — одна строка: без табуляции и переводов строки (ревью 013, T055).
  // eslint-disable-next-line no-control-regex -- управляющие знаки и есть предмет проверки
  .refine((label) => !/[\u0000-\u001F\u007F]/.test(label), {
    message: 'В названии — управляющий знак или перевод строки',
  });

/** «Добавить столбец» — новое текстовое поле протокола для всех; ключ выдаёт сервер. */
export const FieldsAddCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('fields.add'),
  label: FieldLabelSchema,
});

/** Переименование дополнительного поля — для всех. */
export const FieldsRenameCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('fields.rename'),
  key: ExtraFieldKeySchema,
  label: FieldLabelSchema,
});

/** Название снимка недели (016: FR-005). */
export const SNAPSHOT_TITLE_MAX = 120;

/**
 * «Зафиксировать неделю» (016: research Р2): снимок рабочей недели и следующая рабочая неделя.
 * Принимается только на актуальной ревизии протокола; следующая неделя — позже текущей.
 */
export const WeekSnapshotCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('week.snapshot'),
  title: z
    .string()
    .max(SNAPSHOT_TITLE_MAX)
    .refine((title) => title.trim().length > 0, { message: 'Введите название' })
    .refine((title) => withoutControlChars(title) === title && !/[\n\r\t]/.test(title), {
      message: 'В названии — управляющий знак или перевод строки',
    }),
  nextWeekDate: DateSchema,
});

/**
 * «Восстановить эту версию» (029: FR-018–FR-023, research Р5; VER-05): протокол целиком становится
 * таким, как на ревизии `revision`, одной правкой. База — ревизия протокола из предпросмотра:
 * протокол изменили после него — MIN-020. Ревизию дерева не проверяет (сверяет всю ревизию).
 */
export const VersionRestoreCommandSchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
  kind: z.literal('version.restore'),
  revision: RevisionSchema,
});

/**
 * «Вернуть выделенное» (029: FR-026–FR-028, research Р5; VER-06): выделенные ячейки и строки
 * становятся такими, как на ревизии `revision`. Применяется к текущему состоянию; поле выделения,
 * изменённое после `baseRevision` другим значением, — MIN-020.
 */
export const VersionRevertCommandSchema = z
  .strictObject({
    commandId: IdSchema,
    baseRevision: RevisionSchema,
    kind: z.literal('version.revert'),
    revision: RevisionSchema,
    cells: z.array(VersionCellRefSchema).max(VERSION_REVERT_CELLS_MAX),
    rows: z.array(IdSchema).max(VERSION_REVERT_ROWS_MAX),
  })
  .refine((command) => command.cells.length + command.rows.length > 0, {
    message: 'Выделите ячейки или строки',
  });

/** Отмена своей команды этого протокола — обратное действие (EDIT-14, HIST-08). */
export const CommandUndoCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('command.undo'),
  targetCommandId: IdSchema,
});

/** Повтор — обратное действие к своей отмене. */
export const CommandRedoCommandSchema = z.strictObject({
  ...structureBase,
  kind: z.literal('command.redo'),
  targetCommandId: IdSchema,
});

export const CommandSchema = z.discriminatedUnion('kind', [
  CellsEditCommandSchema,
  RowsInsertCommandSchema,
  ParentResolveCommandSchema,
  BranchesMoveCommandSchema,
  BranchesIndentCommandSchema,
  BranchesOutdentCommandSchema,
  BranchesCopyCommandSchema,
  BranchesArchiveCommandSchema,
  BranchesRestoreCommandSchema,
  ItemSplitCommandSchema,
  CommandUndoCommandSchema,
  CommandRedoCommandSchema,
  CellsBatchCommandSchema,
  CellsStyleCommandSchema,
  CellsBordersCommandSchema,
  CellsMergeCommandSchema,
  RowsHideCommandSchema,
  FieldsAddCommandSchema,
  FieldsRenameCommandSchema,
  ItemAssigneesCommandSchema,
  ItemContractorsCommandSchema,
  PartiesReplaceCommandSchema,
  PartiesMoveCommandSchema,
  PartiesSplitCommandSchema,
  ItemPartiesCommandSchema,
  PartiesMergeCommandSchema,
  ItemWorkStateCommandSchema,
  WeekSnapshotCommandSchema,
  VersionRestoreCommandSchema,
  VersionRevertCommandSchema,
]);
export type Command = z.infer<typeof CommandSchema>;
export type CommandKind = Command['kind'];

/** Все виды команд таблицы (015: лента «Изменения», research Р3). */
export const commandKinds = [
  'cells.edit',
  'rows.insert',
  'parent.resolve',
  'branches.move',
  'branches.indent',
  'branches.outdent',
  'branches.copy',
  'branches.archive',
  'branches.restore',
  'item.split',
  'command.undo',
  'command.redo',
  'cells.batch',
  'cells.style',
  'cells.borders',
  'cells.merge',
  'rows.hide',
  'fields.add',
  'fields.rename',
  'item.assignees',
  'item.contractors',
  'parties.replace',
  'parties.move',
  'parties.split',
  'item.parties',
  'parties.merge',
  'item.workState',
  'week.snapshot',
  'version.restore',
  'version.revert',
] as const satisfies readonly CommandKind[];
// Новый вид команды без строки здесь — ошибка типов.
type MissingCommandKind = Exclude<CommandKind, (typeof commandKinds)[number]>;
const allCommandKinds: [MissingCommandKind] extends [never] ? true : never = true;
void allCommandKinds;

/**
 * Команды, которые всегда меняют дерево: проверяют ревизию дерева. Отмена и повтор несут
 * базу дерева, а меняют ли дерево — решает цель (012: research, Р6).
 */
export const structureCommandKinds = [
  'rows.insert',
  'parent.resolve',
  'branches.move',
  'branches.indent',
  'branches.outdent',
  'branches.copy',
  'branches.archive',
  'branches.restore',
  'item.split',
  // Скрытие в протоколе дерево не меняет, но сводит выбор к корням по дереву базы (013, Р14).
  'rows.hide',
] as const satisfies readonly CommandKind[];

/** Перевод протокола: команда без базы рабочего состояния — его ещё нет. */
export const TransferBodySchema = z.strictObject({ commandId: IdSchema });

/** Возврат в режим файла: база — текущая ревизия. */
export const RevertBodySchema = z.strictObject({
  commandId: IdSchema,
  baseRevision: RevisionSchema,
});

/** Ответ на принятую команду: новая ревизия и затронутые пункты целиком. */
export const CommandAcceptedSchema = z.object({
  commandId: IdSchema,
  revision: RevisionSchema,
  structureRevision: RevisionSchema,
  items: z.array(ItemRowSchema),
  /** Новые пункты команды в порядке дерева (вставка, копия, разделение). */
  created: z.array(IdSchema),
  /** true — повтор уже принятой команды: эффекта нет, ответ — прежний. */
  replayed: z.boolean(),
  /** Колонки протокола после «Доп. полей» (013: fields.add, fields.rename). */
  columns: z.array(ProtocolColumnSchema).optional(),
  /** Снимок, созданный фиксацией недели (016: week.snapshot). */
  snapshot: z.object({ id: IdSchema, weekDate: DateSchema, title: z.string() }).optional(),
  /**
   * Смены вида справочника, которые сделала parties.move (0.20.1): отмена переноса возвращает
   * и их (`directory.kindChanges.undo`), иначе запись осталась бы другого вида.
   */
  kindChanges: z.array(IdSchema).optional(),
  /** Пар «тот же?», которые parties.merge предложила справочнику (028: FR-025). */
  pairs: z.number().int().min(0).optional(),
});
export type CommandAccepted = z.infer<typeof CommandAcceptedSchema>;
